import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";
import { registerDupResolver } from "@/lib/dupCheck";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const REASONS = { exact: "Stesso numero e controparte", date_amount: "Data vicina e stesso importo", number_piva: "Stesso numero e P.IVA",
  number_name: "Stesso numero e nome", numero: "Stesso numero e fornitore", data_importo: "Stessa data e importo" };

const amountOf = (kind, d) => {
  if (kind === "ddt") return d.importo_stimato;
  if (d.totale != null) return d.totale;
  return (Number(d.imponibile) || 0) * (1 + (Number(d.iva_percentuale) || 0) / 100);
};

const DocBox = ({ title, kind, d, selected, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
          className={`w-full text-left rounded-md border p-3 text-xs space-y-0.5 transition-colors ${selected ? "border-primary bg-primary/5" : "hover:bg-secondary"}`}>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{title}</div>
    <div className="font-bold text-sm">n. {d.numero || "s.n."} · {d.data}</div>
    {kind === "fattura" && <div>{d.controparte}</div>}
    {d.descrizione && <div className="text-muted-foreground truncate">{d.descrizione}</div>}
    <div className="font-semibold tabular-nums">{fmt(amountOf(kind, d))}</div>
    {d.reason && <div className="text-amber-700">{REASONS[d.reason] || d.reason}</div>}
  </button>
);

export const DuplicateResolver = () => {
  const [req, setReq] = useState(null);
  const [target, setTarget] = useState(null);
  const resolveRef = useRef(null);

  useEffect(() => {
    registerDupResolver((r) => new Promise((resolve) => {
      resolveRef.current = resolve;
      setTarget(r.duplicates[0]?.id || null);
      setReq(r);
    }));
    return () => registerDupResolver(null);
  }, []);

  const finish = (action) => {
    resolveRef.current?.({ action, target_id: target });
    resolveRef.current = null;
    setReq(null);
  };

  const label = req?.kind === "ddt" ? "DDT" : "fattura";
  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o && req) finish("skip"); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dup-resolver-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Possibile doppione</DialogTitle>
          <DialogDescription>Esiste già una {label} simile. Cosa vuoi fare?</DialogDescription>
        </DialogHeader>
        {req && (
          <div className="grid sm:grid-cols-2 gap-3">
            <DocBox title="Nuovo documento" kind={req.kind} d={req.nuovo} testid="dup-new-doc" />
            <div className="space-y-2" data-testid="dup-existing-list">
              {req.duplicates.map((d) => (
                <DocBox key={d.id} title="Già presente" kind={req.kind} d={d} selected={target === d.id}
                        onClick={() => setTarget(d.id)} testid={`dup-existing-${d.id}`} />
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => finish("skip")} data-testid="dup-skip">Salta / Annulla</Button>
          <Button variant="outline" onClick={() => finish("add")} data-testid="dup-add">Aggiungi lo stesso</Button>
          <Button variant="destructive" onClick={() => finish("overwrite")} disabled={!target} data-testid="dup-overwrite">Sovrascrivi</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
